import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, Ban } from "lucide-react";
import { Card, Quiet } from "@/components/hub/Shell";

/**
 * DeclineInsights — a plain reading of why desks are passing. Bars, counts, one flag.
 * Used on the Hub overview (all desks) and on a single lender's record.
 */
export function DeclineInsights({ path, days = 90, showLenders = true, title = "Why desks are passing" }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(path, { params: { days } }); setData(r.data); }
    catch { setData(null); }
  }, [path, days]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const top = (data.overall || []).slice(0, 6);
  const flagged = (data.by_lender || []).filter((l) => l.routing_flag);

  return (
    <Card label={title} Icon={Ban} testId="decline-insights"
          right={<span className="text-[11px] tabular-nums text-slate-400">Last {data.days} days · {data.total_declines} decline{data.total_declines === 1 ? "" : "s"}</span>}>
      {data.total_declines === 0 ? (
        <Quiet testId="decline-insights-empty">
          No declines recorded in this period — nothing to read into yet.
        </Quiet>
      ) : (
        <>
          <div className="mt-4 space-y-2.5">
            {top.map((r) => (
              <div key={r.reason} data-testid={`decline-reason-row`}>
                <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
                  <span className="text-slate-700 truncate">{r.reason}</span>
                  <span className="shrink-0 font-mono tabular-nums text-slate-500">{r.count} · {r.pct}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-slate-700" style={{ width: `${Math.max(3, r.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>

          {showLenders && (data.by_lender || []).length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">By desk</div>
              <div className="mt-1 divide-y divide-slate-100">
                {data.by_lender.slice(0, 8).map((l) => (
                  <div key={l.lender_id} className="flex items-start justify-between gap-3 py-2.5"
                       data-testid={`decline-by-lender-${l.lender_id}`}>
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-slate-900">{l.company_name || "Unknown desk"}</div>
                      {l.top_reason && (
                        <div className="truncate text-[11.5px] text-slate-400">
                          mostly &ldquo;{l.top_reason.reason}&rdquo; · {l.top_reason.pct}%
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {l.routing_flag && (
                        <span className="inline-flex h-5 items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] px-2 text-[10px] font-semibold text-[#3B63A8]"
                              title="Same reason again and again — we're probably sending them the wrong thing">
                          <AlertTriangle className="size-2.5" /> check routing
                        </span>
                      )}
                      <span className="font-mono text-[12px] tabular-nums text-slate-500">{l.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {flagged.length > 0 && (
            <div className="mt-4 rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3 text-[12.5px] leading-relaxed text-[#24457F]" data-testid="decline-routing-flag">
              <b>{flagged.length} desk{flagged.length === 1 ? "" : "s"} keep passing for the same reason.</b>{" "}
              Worth tightening their criteria or the routing rule that feeds them — the cases aren't the problem.
            </div>
          )}
        </>
      )}
    </Card>
  );
}
